<script lang="ts" setup>
import type { IGameCategory } from '~/interfaces/dto/games/response';
import CategoryCard from './category-card.vue';
import GenericError from '../user-interface/generic-error.vue';
import { useIntersectionObserver } from '@vueuse/core';
import Button from '../design-system/button.vue';
import type { CardProps } from '~/interfaces/components/category-card';
const site = useSiteStore();

interface ICategoryGridProps {
  inModal?: boolean;
  games: IGameCategory[];
  loading?: boolean;
  loadingMore?: boolean;
  lazyLoadMore?: boolean;
  maxSize?: number;
  cardProps?: Partial<CardProps>;
  container?: boolean;
}

interface ICategoryGridEmits {
  (e: 'lazy-load', skip: number): void;
}

const gameCard = ref();

const props = withDefaults(defineProps<ICategoryGridProps>(), {
  games: [],
  inModal: false,
  loading: false,
  loadingMore: false,
  lazyLoadMore: false,
  maxSize: 50,
  cardProps: {},
  container: true,
});

const emits = defineEmits<ICategoryGridEmits>();

watch(
  () => gameCard.value,
  (v) => {
    nextTick(() => {
      const lastCard = v[v?.length - 1];

      const { stop } = useIntersectionObserver(
        lastCard,
        async ([{ isIntersecting }]) => {
          if (isIntersecting) {
            if (v?.length >= props?.maxSize) {
              stop();
            }
            emits('lazy-load', v.length);
            stop();
          }
        },
        {
          threshold: 0.001,
        },
      );
    });
  },
  {
    deep: true,
  },
);
</script>
<template>
  <div :class="props.container ? 'container' : ''">
    <div
      v-if="games.length && !loading"
      class="grid pb-5 gap-2 grid-cols-3 md:grid-cols-4"
      :class="{
        'lg:grid-cols-6 xl:grid-cols-8 mt-2': !inModal,
        'p-3': inModal,
        '!pb-0': 'no-padding-bottom' in $attrs,
      }"
    >
      <CategoryCard
        v-for="(game, index) in games"
        :id="game.id"
        ref="gameCard"
        :key="`${game.name}-${index}`"
        :index="index"
        :image="game.tileImage"
        :title="game.name"
        :subtitle="game.provider"
        :game="game"
        v-bind="cardProps"
      />

      <div v-if="loadingMore" class="relative">
        <LazyUserInterfaceGenericLoader container />
      </div>
    </div>
    <GenericError v-else state="info">
      <div
        class="flex items-center justify-between text-base-priority text-left w-full"
      >
        <div class="mr-1">
          <div>{{ $t('no-results') }}</div>
          <div>{{ $t('no-results-message') }}</div>
        </div>
        <Button element-name="filter-reset" @click="site.deactivateModal()">
          {{ $t('reset-filters') }}
        </Button>
      </div>
    </GenericError>
  </div>
</template>
